// imports
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { initialWaterDataState } from '../store/waterDataSlice';

// root url
const baseUrl = import.meta.env.VITE_NWDC_S3_URL;

// Available Water Data configuration
export type BinType = (string | number)[][];

type AWDVizConfig = {
  units: string;
  abbreviated_units: string;
  colors: string[];
  bins: BinType;
  bin_labels: string[];
  var_name: string;
};

type BinTypeTransformed = (string | number)[];

export type AWDVizConfigTransformed = {
  units: string;
  abbreviatedUnits: string;
  colors: string[];
  bins: BinType;
  tooltipBins: BinTypeTransformed;
  bin_labels: string[];
  var_name: string;
};

// Extract bins from list of lists
export function processColorBins(bins: BinType) {
  return bins.map(([first, second]) => (first === '-inf' ? second : first));
}

// Expose query-string builder for testing defaults
export function buildAWDConfigQueryString(model?: string, variable?: string) {
  model = !model ? initialWaterDataState.selectedModel : model;
  variable = !variable ? initialWaterDataState.selectedVariable : variable;
  return `${model}_${variable}.json`;
}

// Expose transform function for testing mapping
export function transformAWDVizConfigResponse(
  response: AWDVizConfig
): AWDVizConfigTransformed {
  const newBins = processColorBins(response.bins);
  return {
    units: response.units,
    abbreviatedUnits: response.abbreviated_units,
    colors: response.colors,
    bins: response.bins,
    tooltipBins: newBins,
    bin_labels: response.bin_labels,
    var_name: response.var_name,
  };
}

// awsConfigAPI
export const awdVizConfigApi = createApi({
  reducerPath: 'awdVizConfigApi',
  baseQuery: fetchBaseQuery({ baseUrl: `${baseUrl}/config/mapPipeline/` }),
  endpoints: (build) => ({
    getAWDConfig: build.query<
      AWDVizConfigTransformed,
      { model: string; variable: string }
    >({
      query: ({ model, variable }) => {
        // provide defaults in case of null
        const queryString = buildAWDConfigQueryString(model, variable);
        return queryString;
      },
      transformResponse: (response: AWDVizConfig) => {
        return transformAWDVizConfigResponse(response);
      },
    }),
  }),
});

export const { useGetAWDConfigQuery } = awdVizConfigApi;
